import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Stack,
  Chip,
  TextField,
  Select,
  MenuItem,
  Button,
  Snackbar,
  Alert,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ReceiptIcon from '@mui/icons-material/Receipt';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import InventoryIcon from '@mui/icons-material/Inventory';
import PeopleIcon from '@mui/icons-material/People';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PaymentIcon from '@mui/icons-material/Payment';
import BarChartIcon from '@mui/icons-material/BarChart';
import PieChartIcon from '@mui/icons-material/PieChart';
import TimelineIcon from '@mui/icons-material/Timeline';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';

const reports = [
  {
    id: 'profit-loss',
    title: 'Profit & Loss Report',
    description: 'Comprehensive P&L statement with revenue, COGS, expenses',
    icon: TrendingUpIcon,
    color: '#1e88e5',
    category: 'Financial',
    endpoint: '/api/reports/profit-loss',
  },
  {
    id: 'sales-summary',
    title: 'Sales Summary Report',
    description: 'Total sales by period, customer, product',
    icon: AssessmentIcon,
    color: '#43a047',
    category: 'Sales',
    endpoint: '/api/reports/sales-summary',
  },
  {
    id: 'purchase-summary',
    title: 'Purchase Summary Report',
    description: 'Total purchases by vendor, item, period',
    icon: ShoppingCartIcon,
    color: '#8e24aa',
    category: 'Purchase',
    endpoint: '/api/reports/purchase-summary',
  },
  {
    id: 'pos-sales',
    title: 'POS Sales Report',
    description: 'Point of sale transactions and daily sales',
    icon: PointOfSaleIcon,
    color: '#fb8c00',
    category: 'POS',
    endpoint: '/api/reports/pos-sales',
  },
  {
    id: 'inventory-valuation',
    title: 'Inventory Valuation',
    description: 'Stock value, quantities, and item-wise breakdown',
    icon: InventoryIcon,
    color: '#00acc1',
    category: 'Inventory',
    endpoint: '/api/reports/inventory-valuation',
  },
  {
    id: 'customer-ledger',
    title: 'Customer Ledger',
    description: 'Customer-wise outstanding, payments, invoices',
    icon: PeopleIcon,
    color: '#d32f2f',
    category: 'Accounts',
    endpoint: '/api/reports/customer-ledger',
  },
  {
    id: 'vendor-ledger',
    title: 'Vendor Ledger',
    description: 'Vendor-wise payables, payments, purchase history',
    icon: LocalShippingIcon,
    color: '#5e35b1',
    category: 'Accounts',
    endpoint: '/api/reports/vendor-ledger',
  },
  {
    id: 'gst-report',
    title: 'GST Report',
    description: 'GSTR-1, GSTR-3B summary with tax breakup',
    icon: ReceiptIcon,
    color: '#7cb342',
    category: 'Tax',
    endpoint: '/api/reports/gst-report',
  },
  {
    id: 'payment-received',
    title: 'Payment Received Report',
    description: 'All payments received from customers',
    icon: PaymentIcon,
    color: '#039be5',
    category: 'Accounts',
    endpoint: '/api/reports/payments-received',
  },
  {
    id: 'payment-made',
    title: 'Payment Made Report',
    description: 'All payments made to vendors',
    icon: MonetizationOnIcon,
    color: '#e53935',
    category: 'Accounts',
    endpoint: '/api/reports/payments-made',
  },
  {
    id: 'expense-report',
    title: 'Expense Report',
    description: 'Category-wise expenses and spending trends',
    icon: AccountBalanceWalletIcon,
    color: '#fb8c00',
    category: 'Expenses',
    endpoint: '/api/reports/expense-report',
  },
  {
    id: 'cash-flow',
    title: 'Cash Flow Statement',
    description: 'Cash inflows and outflows analysis',
    icon: AccountBalanceIcon,
    color: '#00897b',
    category: 'Financial',
    endpoint: '/api/reports/cash-flow',
  },
  {
    id: 'balance-sheet',
    title: 'Balance Sheet',
    description: 'Assets, liabilities, and equity snapshot',
    icon: BarChartIcon,
    color: '#3949ab',
    category: 'Financial',
    endpoint: '/api/reports/balance-sheet',
  },
  {
    id: 'sales-by-product',
    title: 'Sales by Product',
    description: 'Product-wise sales performance',
    icon: PieChartIcon,
    color: '#f4511e',
    category: 'Sales',
    endpoint: '/api/reports/sales-by-product',
  },
  {
    id: 'sales-by-customer',
    title: 'Sales by Customer',
    description: 'Customer-wise sales and revenue',
    icon: PeopleIcon,
    color: '#6a1b9a',
    category: 'Sales',
    endpoint: '/api/reports/sales-by-customer',
  },
  {
    id: 'purchase-by-vendor',
    title: 'Purchase by Vendor',
    description: 'Vendor-wise purchase analysis',
    icon: LocalShippingIcon,
    color: '#c62828',
    category: 'Purchase',
    endpoint: '/api/reports/purchase-by-vendor',
  },
  {
    id: 'stock-summary',
    title: 'Stock Summary Report',
    description: 'Current stock levels, low stock alerts',
    icon: InventoryIcon,
    color: '#2e7d32',
    category: 'Inventory',
    endpoint: '/api/reports/stock-summary',
  },
  {
    id: 'daily-sales',
    title: 'Daily Sales Report',
    description: 'Day-wise sales trends and patterns',
    icon: TimelineIcon,
    color: '#1565c0',
    category: 'Sales',
    endpoint: '/api/reports/daily-sales',
  },
  {
    id: 'monthly-sales',
    title: 'Monthly Sales Report',
    description: 'Month-wise sales summary and comparison',
    icon: AssessmentIcon,
    color: '#558b2f',
    category: 'Sales',
    endpoint: '/api/reports/monthly-sales',
  },
  {
    id: 'pos-daily',
    title: 'POS Daily Summary',
    description: 'Daily POS transactions and cash register',
    icon: PointOfSaleIcon,
    color: '#ef6c00',
    category: 'POS',
    endpoint: '/api/reports/pos-daily',
  },
];

export default function Reports() {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const toLocalDate = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
  const today = toLocalDate(new Date());
  const lastMonth = toLocalDate(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000));
  const [startDate, setStartDate] = useState(lastMonth);
  const [endDate, setEndDate] = useState(today);
  const [downloadStatus, setDownloadStatus] = useState('');

  const categories = ['All', ...new Set(reports.map((r) => r.category))];

  const filteredReports =
    selectedCategory === 'All'
      ? reports
      : reports.filter((r) => r.category === selectedCategory);

  const handleDownload = async (report, format) => {
    if (!report.endpoint) {
      setDownloadStatus({ message: `${report.title} - Coming soon!`, severity: 'info' });
      setTimeout(() => setDownloadStatus(''), 3000);
      return;
    }

    setDownloadStatus({ message: `Generating ${report.title} (${format.toUpperCase()})...`, severity: 'info' });
    try {
      const url = `${report.endpoint}?start=${startDate}&end=${endDate}&format=${format}`;
      const response = await fetch(url, { credentials: 'include' });

      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: 'Failed to generate report' }));
        throw new Error(err.error || 'Failed to generate report');
      }

      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        const err = await response.json();
        throw new Error(err.error || 'Server returned JSON instead of file');
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      const ext = format === 'pdf' ? 'pdf' : 'csv';
      a.download = `${report.id}-${startDate}-${endDate}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);

      setDownloadStatus({ message: `${report.title} downloaded successfully!`, severity: 'success' });
      setTimeout(() => setDownloadStatus(''), 3000);
    } catch (error) {
      console.error('Download error:', error);
      setDownloadStatus({ message: `Error: ${error.message || 'Failed to download ' + report.title}`, severity: 'error' });
      setTimeout(() => setDownloadStatus(''), 4000);
    }
  };

  return (
    <Box className="reports-page" sx={{ p: 2, width: '100%', maxWidth: '100%', mx: 'auto' }}>
      <Stack className="reports-header" direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Reports & Analytics
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Download comprehensive reports for your business
          </Typography>
        </Box>
      </Stack>

      <Paper className="reports-controls" sx={{ p: 2, mb: 3, border: '1px solid', borderColor: 'divider' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
          <Select
            size="small"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            sx={{ minWidth: 150 }}
          >
            {categories.map((cat) => (
              <MenuItem key={cat} value={cat}>
                {cat}
              </MenuItem>
            ))}
          </Select>

          <TextField
            size="small"
            type="date"
            label="From Date"
            InputLabelProps={{ shrink: true }}
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            sx={{ minWidth: 160 }}
          />

          <TextField
            size="small"
            type="date"
            label="To Date"
            InputLabelProps={{ shrink: true }}
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            sx={{ minWidth: 160 }}
          />

          <Button variant="contained" size="small">
            Apply Filters
          </Button>
        </Stack>
      </Paper>

      {downloadStatus && (
        <Snackbar open={Boolean(downloadStatus)} autoHideDuration={4000} onClose={() => setDownloadStatus('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert onClose={() => setDownloadStatus('')} severity={downloadStatus?.severity || 'info'} variant="filled" sx={{ width: '100%' }}>
            {downloadStatus?.message || downloadStatus}
          </Alert>
        </Snackbar>
      )}

      <Box className="reports-grid"
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: 2,
        }}
      >
        {filteredReports.map((report) => {
          const Icon = report.icon;
          return (
            <Card className="report-card"
              key={report.id}
              sx={{
                '--report-color': report.color,
                border: '1px solid',
                borderColor: 'divider',
                borderLeft: `4px solid ${report.color}`,
                transition: 'all 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4,
                },
              }}
            >
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: `${report.color}15`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Icon sx={{ fontSize: 32, color: report.color }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {report.title}
                        </Typography>
                      </Stack>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {report.description}
                      </Typography>
                      <Chip label={report.category} size="small" sx={{ bgcolor: `${report.color}20`, color: report.color, fontWeight: 600 }} />
                    </Box>
                  </Stack>
                  <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 2 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={(e) => { e.stopPropagation(); handleDownload(report, 'csv'); }}
                      sx={{ color: report.color, borderColor: `${report.color}50`, fontSize: '.65rem', textTransform: 'none' }}
                    >
                      CSV
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<DownloadIcon />}
                      onClick={(e) => { e.stopPropagation(); handleDownload(report, 'pdf'); }}
                      sx={{ bgcolor: report.color, '&:hover': { bgcolor: report.color }, fontSize: '.65rem', textTransform: 'none' }}
                    >
                      PDF
                    </Button>
                  </Stack>
                </CardContent>
            </Card>
          );
        })}
      </Box>

      {filteredReports.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No reports found for selected category
          </Typography>
        </Paper>
      )}
    </Box>
  );
}
